/** O que este aparelho aguenta — medido, não declarado.
 *
 * A detecção antiga perguntava `navigator.deviceMemory` e decidia por ela. Dois
 * problemas, ambos silenciosos:
 *
 * 1. A API não devolve a RAM real. Arredonda para baixo em potência de 2, e no
 *    celular não passa de 8 — um aparelho de 6 GB reporta 4, um de 12 reporta
 *    8. Não existe o valor 6, então comparar com 6 nunca fez o que parecia
 *    fazer. (No Chrome de desktop recente o teto subiu: medindo aqui em
 *    06/08/2026 saiu 16. Mais uma razão para não tratar o número como verdade.)
 * 2. Safari e Firefox não implementam a API. Tratar ausência como "pouca
 *    memória" classificava todo iPhone como o pior aparelho possível,
 *    independente de qual iPhone fosse.
 *
 * Aqui a regra é outra: o que dá para medir, mede-se; o que não dá, diz-se
 * "não dá para saber" — nunca se converte ausência de informação em veredito.
 */

export type Suporte = "sim" | "nao" | "desconhecido";

export interface DiagnosticoAparelho {
  /** Adapter WebGPU de verdade (pedido ao navegador, não inferido do user-agent). */
  webgpu: Suporte;
  /** Nome do adapter, quando o navegador conta. */
  webgpuInfo: string | null;
  /** Maior buffer que a GPU aceita, em MB. Decide que modelo cabe. */
  webgpuMaxBufferMb: number | null;
  /** Núcleos lógicos. -1 quando o navegador não conta. */
  cores: number;
  /** RAM em GB como o navegador reporta: arredondada para baixo em potência de
   *  2. No Android o valor não passa de 8; no Chrome de desktop recente já sai
   *  16 ou 32 — medido aqui em 06/08/2026. Nunca é a RAM real. */
  ramReportadaGb: number | null;
  /** Teto de heap do JS, em MB (só Chrome). */
  heapLimiteMb: number | null;
  /** Espaço que dá para guardar modelo, em MB. */
  armazenamentoLivreMb: number | null;
  /** WebAssembly com SIMD — sem isso a inferência em CPU fica lenta demais. */
  wasmSimd: Suporte;
  /** WebAssembly com threads (precisa de cross-origin isolation). */
  wasmThreads: Suporte;
  /** Milissegundos de um cálculo fixo. É a única medida de velocidade real. */
  provaMs: number | null;
  /** Tipo de conexão, quando informado — pesa no download do modelo. */
  conexao: string | null;
  /** Parece aparelho móvel (só user-agent — é palpite, e vai dito como tal). */
  pareceCelular: boolean;
  /** Maior bloco que a aba conseguiu reservar de fato, em MB. Só é preenchido
   *  quando a pessoa pede — a medição pressiona a memória e pode derrubar o
   *  app, então não roda sozinha ao abrir a tela. */
  tetoAlocavelMb?: number | null;
}

/** Quanto esta aba consegue reservar de verdade.
 *
 * É o número que faltava: `deviceMemory` diz a RAM do APARELHO e
 * `jsHeapSizeLimit` diz o teto do heap JS — e o modelo não vive em nenhum dos
 * dois, vive na memória do WebAssembly. Medido em 06/08/2026, o whisper-small
 * precisou de ~1,45 GB de memória de processo; um aparelho que reporta 4 GB de
 * RAM não chega perto disso numa aba.
 *
 * Cresce em degraus e para no primeiro que falhar. `WebAssembly.Memory` lança
 * RangeError quando não consegue — diferente do OOM do sistema, que mata sem
 * aviso e sem exceção. Por isso dá para medir sem derrubar; ainda assim, é
 * pressão de memória de verdade, e por isso fica atrás de um botão. */
export async function medirTetoAlocavel(): Promise<number | null> {
  const PAGINA = 65536;
  const degraus = [64, 128, 256, 384, 512, 768, 1024];
  let ultimoOk: number | null = null;
  for (const mb of degraus) {
    try {
      const mem = new WebAssembly.Memory({ initial: (mb * 1024 * 1024) / PAGINA });
      // Tocar o primeiro e o último byte força o sistema a entregar as páginas
      // de fato. Sem isso a reserva pode ser só virtual e o número mente.
      const view = new Uint8Array(mem.buffer);
      view[0] = 1;
      view[view.length - 1] = 1;
      ultimoOk = mb;
      // Devolve antes do próximo degrau: medir o teto não pode ser o que
      // derruba a aba.
      await new Promise((r) => setTimeout(r, 60));
    } catch {
      break;
    }
  }
  return ultimoOk;
}

/** Só é "não" quando o navegador respondeu que não. Sem resposta é "desconhecido". */
async function checarWebGpu(): Promise<{
  suporte: Suporte;
  info: string | null;
  maxBufferMb: number | null;
}> {
  const gpu = (navigator as unknown as { gpu?: { requestAdapter(): Promise<unknown> } }).gpu;
  if (!gpu) return { suporte: "nao", info: null, maxBufferMb: null };
  try {
    const adapter = (await gpu.requestAdapter()) as
      | { info?: { vendor?: string; architecture?: string }; limits?: Record<string, number> }
      | null;
    if (!adapter) return { suporte: "nao", info: null, maxBufferMb: null };
    const info = adapter.info
      ? [adapter.info.vendor, adapter.info.architecture].filter(Boolean).join(" ") || null
      : null;
    const bytes = adapter.limits?.maxStorageBufferBindingSize ?? null;
    return {
      suporte: "sim",
      info,
      maxBufferMb: bytes ? Math.round(bytes / (1024 * 1024)) : null,
    };
  } catch {
    // Pedir adapter pode falhar por driver bloqueado. Não é "não tem GPU".
    return { suporte: "desconhecido", info: null, maxBufferMb: null };
  }
}

/** Módulos mínimos que só validam se o recurso existe no runtime. */
const WASM_SIMD = Uint8Array.from([
  0, 97, 115, 109, 1, 0, 0, 0, 1, 5, 1, 96, 0, 1, 123, 3, 2, 1, 0, 10, 10, 1, 8, 0, 65, 0,
  253, 15, 253, 98, 11,
]);

function checarWasmSimd(): Suporte {
  try {
    return WebAssembly.validate(WASM_SIMD) ? "sim" : "nao";
  } catch {
    return "desconhecido";
  }
}

function checarWasmThreads(): Suporte {
  try {
    if (typeof SharedArrayBuffer === "undefined") return "nao";
    // SharedArrayBuffer existe mas só serve com a página isolada.
    return (self as unknown as { crossOriginIsolated?: boolean }).crossOriginIsolated
      ? "sim"
      : "nao";
  } catch {
    return "desconhecido";
  }
}

/** Um cálculo fixo, pesado o bastante para separar aparelhos e curto o bastante
 *  para não travar a abertura da tela. Vale como referência entre aparelhos,
 *  não como número absoluto. */
function medirProva(): number {
  const t0 = performance.now();
  const n = 200_000;
  const a = new Float64Array(n);
  for (let i = 0; i < n; i++) a[i] = Math.sqrt(i) * Math.sin(i);
  let s = 0;
  for (let repeticao = 0; repeticao < 12; repeticao++) {
    for (let i = 0; i < n; i++) s += a[i]! * 1.000001;
  }
  // `s` precisa ser lido, senão o motor pode descartar o laço inteiro.
  if (!Number.isFinite(s)) return -1;
  return Math.round(performance.now() - t0);
}

export async function diagnosticar(): Promise<DiagnosticoAparelho> {
  const nav = navigator as unknown as {
    deviceMemory?: number;
    hardwareConcurrency?: number;
    storage?: { estimate(): Promise<{ quota?: number; usage?: number }> };
    connection?: { effectiveType?: string };
    userAgent?: string;
  };

  const webgpu = await checarWebGpu();

  let armazenamentoLivreMb: number | null = null;
  try {
    const est = await nav.storage?.estimate();
    if (est?.quota != null) {
      armazenamentoLivreMb = Math.round(((est.quota - (est.usage ?? 0)) / (1024 * 1024)));
    }
  } catch { /* sem permissão: fica desconhecido */ }

  const heap = (performance as unknown as { memory?: { jsHeapSizeLimit?: number } }).memory;

  return {
    webgpu: webgpu.suporte,
    webgpuInfo: webgpu.info,
    webgpuMaxBufferMb: webgpu.maxBufferMb,
    cores: nav.hardwareConcurrency ?? -1,
    // null (e não 0) quando o navegador não conta — ausência não é escassez.
    ramReportadaGb: typeof nav.deviceMemory === "number" ? nav.deviceMemory : null,
    heapLimiteMb: heap?.jsHeapSizeLimit ? Math.round(heap.jsHeapSizeLimit / (1024 * 1024)) : null,
    armazenamentoLivreMb,
    wasmSimd: checarWasmSimd(),
    wasmThreads: checarWasmThreads(),
    provaMs: medirProva(),
    conexao: nav.connection?.effectiveType ?? null,
    pareceCelular: /Android|iPhone|iPad|iPod|Mobile|Tablet/i.test(nav.userAgent ?? ""),
  };
}

/** Frase única sobre o que o aparelho aguenta, para quem não quer ler tabela.
 *  Fala de evidência, não de nota: "não deu para medir" é resposta válida. */
export function resumo(d: DiagnosticoAparelho): string {
  if (d.webgpu === "sim") {
    const buf = d.webgpuMaxBufferMb ? ` e aceita modelo de até ~${d.webgpuMaxBufferMb} MB por vez` : "";
    return `Este aparelho tem GPU disponível para o navegador${buf}. Dá para rodar os modelos maiores.`;
  }
  if (d.webgpu === "desconhecido") {
    return "Não deu para falar com a GPU — pode ser driver bloqueado. A inferência vai usar o processador.";
  }
  if (d.wasmSimd === "sim") {
    return "Sem GPU para o navegador, mas o processador tem SIMD. Os modelos pequenos rodam; os grandes vão demorar.";
  }
  return "Sem GPU e sem SIMD. Só vale a pena o que for bem pequeno — o resto vai travar a tela.";
}
